import {
  VOLUME_DIRECTORY,
  VolumeError,
  type VolumeSink,
  type VolumeWriterRequest,
  type VolumeWriterResponse,
} from "./volume-core";

export { VOLUME_DIRECTORY };

export interface OpfsVolumeSink {
  sink: VolumeSink;
  kind: "worker" | "writable";
}

type WithoutId<T> = T extends unknown ? Omit<T, "id"> : never;
type WriterRequest = WithoutId<VolumeWriterRequest>;

interface Pending {
  resolve(value: unknown): void;
  reject(error: unknown): void;
}

/** FileSystemSyncAccessHandle을 쓰는 워커 RPC. */
class WorkerVolumeSink implements VolumeSink {
  private readonly worker: Worker;
  private readonly pending = new Map<number, Pending>();
  private sequence = 0;
  private closed = false;
  private failed = false;

  constructor() {
    this.worker = new Worker(new URL("./volume-writer.worker.ts", import.meta.url), {
      type: "module",
      name: "beoptong-volume-writer",
    });
    this.worker.addEventListener("message", this.onMessage);
    this.worker.addEventListener("error", this.onError);
    this.worker.addEventListener("messageerror", this.onError);
  }

  private readonly onMessage = (event: MessageEvent<VolumeWriterResponse>): void => {
    const response = event.data;
    const waiter = this.pending.get(response.id);
    if (!waiter) return;
    this.pending.delete(response.id);
    if (response.ok) waiter.resolve(response.result);
    else waiter.reject(new VolumeError("storage", response.error));
  };

  private readonly onError = (event: Event): void => {
    const message =
      "message" in event && typeof event.message === "string" && event.message
        ? event.message
        : "volume writer failed";
    const error = new VolumeError("storage", message);
    this.failed = true;
    for (const waiter of this.pending.values()) waiter.reject(error);
    this.pending.clear();
  };

  private call<T>(request: WriterRequest, transfer: Transferable[] = []): Promise<T> {
    if (this.closed || this.failed) {
      return Promise.reject(new VolumeError("storage", "volume sink closed"));
    }
    const id = ++this.sequence;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      this.worker.postMessage({ id, ...request } as VolumeWriterRequest, transfer);
    });
  }

  ping(): Promise<void> {
    return this.call<void>({ op: "ping" });
  }

  size(file: string): Promise<number | null> {
    return this.call<number | null>({ op: "size", file });
  }

  read(file: string, offset: number, length: number): Promise<Uint8Array> {
    return this.call<Uint8Array>({ op: "read", file, offset, length });
  }

  write(file: string, offset: number, bytes: Uint8Array): Promise<void> {
    const buffer = bytes.buffer;
    if (
      buffer instanceof ArrayBuffer &&
      bytes.byteOffset === 0 &&
      bytes.byteLength === buffer.byteLength
    ) {
      // 복사 없이 transfer한다. 호출자는 이 버퍼를 다시 쓰지 않는다.
      return this.call<void>({ op: "write", file, offset, bytes: buffer }, [buffer]);
    }
    const copy = bytes.buffer.slice(
      bytes.byteOffset,
      bytes.byteOffset + bytes.byteLength,
    ) as ArrayBuffer;
    return this.call<void>({ op: "write", file, offset, bytes: copy }, [copy]);
  }

  truncate(file: string, size: number): Promise<void> {
    return this.call<void>({ op: "truncate", file, size });
  }

  remove(file: string): Promise<void> {
    return this.call<void>({ op: "remove", file });
  }

  flush(file: string): Promise<void> {
    return this.call<void>({ op: "flush", file });
  }

  promote(from: string, to: string): Promise<void> {
    return this.call<void>({ op: "promote", from, to });
  }

  async close(): Promise<void> {
    if (this.closed) return;
    try {
      await this.call<void>({ op: "close" });
    } catch {
      // 이미 죽은 워커는 그냥 종료한다.
    }
    this.closed = true;
    this.worker.terminate();
    for (const waiter of this.pending.values()) {
      waiter.reject(new VolumeError("storage", "volume sink closed"));
    }
    this.pending.clear();
  }
}

/**
 * 폴백: 워커를 만들 수 없을 때(혹은 SAH 미지원) 메인 스레드에서 `createWritable()`을 쓴다.
 * 청크 단위로 쓰고 닫으므로 메모리는 8MB로 제한되지만, 쓰기 순간 메인 스레드를 쓴다.
 */
class WritableVolumeSink implements VolumeSink {
  private directoryPromise: Promise<FileSystemDirectoryHandle> | null = null;
  private readonly streams = new Map<string, FileSystemWritableFileStream>();

  private directory(): Promise<FileSystemDirectoryHandle> {
    if (!this.directoryPromise) {
      this.directoryPromise = navigator.storage
        .getDirectory()
        .then((root) => root.getDirectoryHandle(VOLUME_DIRECTORY, { create: true }));
    }
    return this.directoryPromise;
  }

  private async fileHandle(name: string, create: boolean): Promise<FileSystemFileHandle> {
    return (await this.directory()).getFileHandle(name, { create });
  }

  private async stream(name: string): Promise<FileSystemWritableFileStream> {
    const existing = this.streams.get(name);
    if (existing) return existing;
    const handle = await this.fileHandle(name, true);
    const stream = await handle.createWritable({ keepExistingData: true });
    this.streams.set(name, stream);
    return stream;
  }

  private async closeStream(name: string): Promise<void> {
    const stream = this.streams.get(name);
    if (!stream) return;
    this.streams.delete(name);
    await stream.close();
  }

  private async removeEntry(name: string): Promise<void> {
    const dir = await this.directory();
    try {
      await dir.removeEntry(name);
    } catch (error) {
      if ((error as DOMException)?.name !== "NotFoundError") throw error;
    }
  }

  async ping(): Promise<void> {
    await this.directory();
  }

  async size(file: string): Promise<number | null> {
    if (this.streams.has(file)) return null;
    try {
      const handle = await this.fileHandle(file, false);
      return (await handle.getFile()).size;
    } catch (error) {
      if ((error as DOMException)?.name === "NotFoundError") return null;
      throw error;
    }
  }

  async read(file: string, offset: number, length: number): Promise<Uint8Array> {
    await this.closeStream(file);
    const handle = await this.fileHandle(file, false);
    const blob = (await handle.getFile()).slice(offset, offset + length);
    return new Uint8Array(await blob.arrayBuffer());
  }

  async write(file: string, offset: number, bytes: Uint8Array): Promise<void> {
    const stream = await this.stream(file);
    await stream.seek(offset);
    await stream.write(bytes as unknown as BufferSource);
  }

  async truncate(file: string, size: number): Promise<void> {
    const stream = await this.stream(file);
    await stream.truncate(size);
  }

  async remove(file: string): Promise<void> {
    await this.closeStream(file);
    await this.removeEntry(file);
  }

  async flush(file: string): Promise<void> {
    await this.closeStream(file);
  }

  async promote(from: string, to: string): Promise<void> {
    await this.closeStream(from);
    const source = (await this.fileHandle(from, false)) as FileSystemFileHandle & {
      move?(name: string): Promise<void>;
    };
    if (typeof source.move === "function") {
      try {
        await source.move(to);
        return;
      } catch {
        // move 미지원/실패 → 복사
      }
    }
    await this.removeEntry(to);
    const dest = await this.fileHandle(to, true);
    const writable = await dest.createWritable();
    await (await source.getFile()).stream().pipeTo(writable);
    await this.removeEntry(from);
  }

  async close(): Promise<void> {
    for (const name of [...this.streams.keys()]) await this.closeStream(name);
  }
}

/**
 * OPFS sink를 만든다. 워커(SAH) 우선, 실패하면 `createWritable()` 폴백.
 * 폴백 여부는 반환값 `kind`로 확인할 수 있다.
 */
export async function createOpfsVolumeSink(options?: {
  preferWorker?: boolean;
}): Promise<OpfsVolumeSink> {
  if (options?.preferWorker !== false) {
    let worker: WorkerVolumeSink | null = null;
    try {
      worker = new WorkerVolumeSink();
      await worker.ping();
      return { sink: worker, kind: "worker" };
    } catch (error) {
      console.warn("[beoptong] OPFS worker unavailable; falling back to createWritable()", error);
      await worker?.close();
    }
  }

  const fallback = new WritableVolumeSink();
  try {
    await fallback.ping();
  } catch (error) {
    await fallback.close();
    throw new VolumeError(
      "storage",
      error instanceof Error ? error.message : "OPFS unavailable",
    );
  }
  return { sink: fallback, kind: "writable" };
}
